import React, { useState, useEffect, useCallback, useRef } from 'react';
import { logs } from './api';
import { formatDateTime, relativeTime } from './format';
import './Portal.css';

export default function LogsPanel() {
  const [entries, setEntries] = useState([]);
  const [counts, setCounts] = useState(null);
  const [level, setLevel] = useState('');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(200);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastLoaded, setLastLoaded] = useState(null);
  const [confirmingClear, setConfirmingClear] = useState(false);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const result = await logs.list({ level, q: search, limit });
      if (!mounted.current) return;
      setEntries(result.entries);
      setCounts(result.counts);
      setLastLoaded(new Date());
      setError(null);
    } catch (err) {
      if (mounted.current) setError(err.message);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [level, search, limit]);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [autoRefresh, load]);

  const handleClear = async () => {
    if (!confirmingClear) {
      setConfirmingClear(true);
      return;
    }
    try {
      await logs.clear();
      setConfirmingClear(false);
      load();
    } catch (err) {
      setError(err.message);
      setConfirmingClear(false);
    }
  };

  return (
    <div>
      {counts && (
        <div className="pt-checks" style={{ marginBottom: 18 }}>
          <div className="pt-check is-ok">
            <div className="pt-check-label">Total entries</div>
            <div className="pt-check-detail">{counts.total} in the current log file</div>
          </div>
          <div className={'pt-check is-' + (counts.warn > 0 ? 'warn' : 'ok')}>
            <div className="pt-check-label">Warnings</div>
            <div className="pt-check-detail">{counts.warn} logged</div>
          </div>
          <div className={'pt-check is-' + (counts.errors_24h > 0 ? 'down' : 'ok')}>
            <div className="pt-check-label">Errors (24h)</div>
            <div className="pt-check-detail">
              {counts.errors_24h} in the last day · {counts.error} total
            </div>
          </div>
        </div>
      )}

      <div className="pt-log-toolbar">
        <input
          className="pt-input"
          type="search"
          placeholder="Search messages…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="pt-select" value={level} onChange={(e) => setLevel(e.target.value)}>
          <option value="">All levels</option>
          <option value="info">Info only</option>
          <option value="warn">Warnings only</option>
          <option value="error">Errors only</option>
        </select>
        <select className="pt-select" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
          <option value={100}>Last 100</option>
          <option value={200}>Last 200</option>
          <option value={500}>Last 500</option>
          <option value={1000}>Last 1000</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', fontWeight: 700 }}>
          <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
          Live
        </label>
        <button className="pt-btn pt-btn-ghost pt-btn-sm" onClick={load} type="button">
          Refresh
        </button>
        <button className="pt-btn pt-btn-danger pt-btn-sm" onClick={handleClear} type="button">
          {confirmingClear ? 'Click again to confirm' : 'Clear log'}
        </button>
      </div>

      {error && (
        <div className="pt-notice pt-notice-error" style={{ marginBottom: 14 }}>
          {error}
        </div>
      )}

      <div className="pt-log">
        {loading && <div className="pt-log-empty">Loading…</div>}

        {!loading && entries.length === 0 && (
          <div className="pt-log-empty">
            Nothing logged{level || search ? ' for this filter' : ' yet'}.
          </div>
        )}

        {!loading &&
          entries.map((entry, index) => (
            <div className="pt-log-row" key={entry.ts + '-' + index}>
              <span className="pt-log-time" title={formatDateTime(entry.ts)}>
                {formatDateTime(entry.ts)}
              </span>
              <span className={'pt-log-level ' + entry.level}>{entry.level}</span>
              <span className="pt-log-msg">
                {entry.message}
                <span className="pt-log-meta">
                  {entry.user ? entry.user + ' · ' : ''}
                  {entry.ip}
                  {entry.path ? ' · ' + entry.path : ''}
                  {entry.meta && Object.keys(entry.meta).length > 0
                    ? ' · ' + JSON.stringify(entry.meta)
                    : ''}
                </span>
              </span>
            </div>
          ))}
      </div>

      <p className="pt-hint" style={{ marginTop: 10 }}>
        {lastLoaded ? 'Loaded ' + relativeTime(lastLoaded) + '. ' : ''}
        The log holds roughly the last 2 MB of activity and rotates automatically — sign-ins, uploads,
        deletions, invitations, PHP errors and JavaScript errors from visitors' browsers.
      </p>
    </div>
  );
}
